import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    ValidationResult,
    FormValidationSummary,
    FormCheckbox,
    FormTextField,
    FormNumberTextField
} from "sprint-common-web";

import {
    AspectRatiosDataContext,
    AspectRatio
} from "../../../../../../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import {
    AspectTypesDataContext,
    AspectType
} from "../../../../../../../../common/data/lookup-data-providers/AspectTypesDataProvider";
import { StandardsDataContext } from "../../../../../../../../common/data/lookup-data-providers/StandardsDataProvider";
import { Standard } from "../../../../../../../../common/types/Standard";
import {
    ensureVideoFileRecordDetails,
    VideoFileRecordTechnical
} from "../../../../../../../common/file-record-details/FileRecordDetails";
import { validateDuration } from "../../../../../../common/utils/validateDuration";
import { validateResolution } from "../../../../../../common/utils/validateResolution";
import { useFileRecordDetailsState } from "../../../../../hooks/useFileRecordDetailsState";

export interface VideoTechnicalFormProps {
    isLoading: boolean;
    onCancel?(): void;
    validationResult?: ValidationResult;
    onSubmit(technicalInfo: VideoFileRecordTechnical): void;
}

interface VideoTechnicalInfoFormInputs {
    aspectRatio: string;
    aspectType: string;
    duration: string;
    encoding: boolean;
    framerate: string;
    resolution: string;
    standard: string;
    bitrate: number;
}

export const VideoTechnicalForm = (props: VideoTechnicalFormProps): JSX.Element => {
    const { fileRecordDetails } = useFileRecordDetailsState();
    const { aspectRatios } = useContext(AspectRatiosDataContext);
    const { aspectTypes } = useContext(AspectTypesDataContext);
    const { standards } = useContext(StandardsDataContext);

    ensureVideoFileRecordDetails(fileRecordDetails);

    const defaultAspectRatio = aspectRatios.find(
        (aspectRatio: AspectRatio) => aspectRatio.id === fileRecordDetails.technical?.aspectRatio?.id
    );

    const defaultAspectType = aspectTypes.find(
        (aspectType: AspectType) => aspectType.id === fileRecordDetails.technical?.aspectType?.id
    );

    const defaultStandard = standards.find(
        (standard: Standard) => standard.id === fileRecordDetails.technical?.standard?.id
    );

    const { handleSubmit, errors, control } = useForm<VideoTechnicalInfoFormInputs>({
        defaultValues: {
            aspectRatio: defaultAspectRatio?.id,
            aspectType: defaultAspectType?.id,
            duration: fileRecordDetails.technical?.duration,
            encoding: fileRecordDetails.technical?.encoding,
            framerate: fileRecordDetails.technical?.framerate,
            resolution: fileRecordDetails.technical?.resolution,
            standard: defaultStandard?.id,
            bitrate: fileRecordDetails.technical?.videoBitRate
        }
    });

    const closeClearedAndCallOnClose = () => {
        if (props.onCancel) {
            props.onCancel();
        }
    };

    const callOnSubmit = (formInputs: VideoTechnicalInfoFormInputs) => {
        const aspectRatio = aspectRatios.find((ratio) => ratio.id === formInputs.aspectRatio);
        const aspectType = aspectTypes.find((ratio) => ratio.id === formInputs.aspectType);
        const standard = standards.find((item) => item.id === formInputs.standard);

        props.onSubmit({
            aspectRatio,
            aspectType,
            duration: formInputs.duration,
            encoding: formInputs.encoding,
            framerate: formInputs.framerate,
            resolution: formInputs.resolution,
            standard,
            videoBitRate: formInputs.bitrate
        });
    };

    return (
        <Form onSubmit={handleSubmit(callOnSubmit)}>
            <FormFields>
                <FormDropdown
                    options={aspectRatios.map((ratio) => mapToDropdownOption(ratio))}
                    control={control}
                    label="Aspect ratio"
                    name="aspectRatio"
                    errors={errors}
                />
                <FormDropdown
                    options={aspectTypes.map((type) => mapToDropdownOption(type))}
                    control={control}
                    label="Aspect type"
                    name="aspectType"
                    errors={errors}
                />
                <FormCheckbox label="Encoding (Proresolution)" name="encoding" control={control} />
                <FormTextField label="Framerate" name="framerate" control={control} errors={errors} />
                <FormNumberTextField label="Bitrate" name="bitrate" control={control} errors={errors} />{" "}
                <FormTextField
                    rules={{ validate: validateDuration }}
                    label="Duration"
                    name="duration"
                    control={control}
                    errors={errors}
                    mask="99:99:99"
                />
                <FormTextField
                    label="Resolution"
                    name="resolution"
                    control={control}
                    errors={errors}
                    rules={{ validate: validateResolution }}
                />
                <FormDropdown
                    options={standards.map((type) => mapToDropdownOption(type))}
                    control={control}
                    label="Standard"
                    name="standard"
                    errors={errors}
                />
                {props.validationResult && <FormValidationSummary validationResult={props.validationResult} />}
                <FormActions onCancel={closeClearedAndCallOnClose} isLoading={props.isLoading} />
            </FormFields>
        </Form>
    );
};
